import { ComponentProps, FC, ReactElement, useCallback, useMemo } from 'react';
import { Box } from '@altermaind/web/ui';
import {
  Claims,
  FeatureFlag,
  PlatformType,
  ROLES_FROM_PLATFORM_TYPE,
  Roles,
} from '@/_shared/constants';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { getUserProfileCall } from '@/core/state/api';

type FeatureFlagCheckMode = 'every' | 'some';

type Props = {
  children: ReactElement;
  featureFlag?: FeatureFlag | FeatureFlag[];
  featureFlagCheckMode?: FeatureFlagCheckMode;
  claimKey?: Claims | Claims[];
  notCheckWriteOrRead?: boolean;
  roles?: Roles[];
  platform?: PlatformType;
  containerProps?: ComponentProps<typeof Box>;
};

export const ClaimsComponent: FC<Props> = ({
  children,
  featureFlag,
  featureFlagCheckMode,
  claimKey,
  notCheckWriteOrRead,
  roles,
  platform,
  containerProps = {},
}) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isFF = isFeatureEnabled(featureFlag || null, featureFlagCheckMode);
  const { claims, userRole } = getUserProfileCall.hook();

  const removedWriteOrRead = useCallback(
    (key: string) => {
      if (!notCheckWriteOrRead) {
        return key;
      }
      return key && key.replace('.Read', '').replace('.Write', '');
    },
    [notCheckWriteOrRead],
  );

  const effectiveRoles = useMemo<Roles[] | undefined>(() => {
    if (roles !== undefined) {
      return roles;
    }
    if (platform !== undefined) {
      return ROLES_FROM_PLATFORM_TYPE[platform];
    }
    return undefined;
  }, [roles, platform]);

  const showComponent = useMemo(() => {
    let claimKeys: Claims[] = [];
    if (Array.isArray(claimKey)) {
      claimKeys = claimKey;
    } else if (claimKey) {
      claimKeys = [claimKey];
    }
    const internalClaimKeys = claimKeys.map(key => removedWriteOrRead(key));
    const hasNoClaimKey = internalClaimKeys.length === 0;
    const internalClaims =
      claims && claims.length > 0
        ? claims.map(claim => removedWriteOrRead(claim as unknown as string))
        : [];
    const hasMatchingClaim = internalClaimKeys.some(key => internalClaims.includes(key));

    const hasRole = userRole ? effectiveRoles?.includes(userRole) : false;

    if (hasNoClaimKey && hasRole && effectiveRoles !== undefined && featureFlag === undefined) {
      return true;
    }
    if (hasNoClaimKey && !hasRole && effectiveRoles !== undefined && featureFlag === undefined) {
      return false;
    }
    if (isFF === undefined && hasNoClaimKey) {
      return true;
    }
    if (isFF && hasNoClaimKey && !hasRole && effectiveRoles !== undefined) {
      return false;
    }
    if (isFF && hasNoClaimKey) {
      return true;
    }
    if (!isFF && isFF !== undefined) {
      return false;
    }
    if (isFF && !hasNoClaimKey && !hasMatchingClaim) {
      return false;
    }
    if (effectiveRoles !== undefined) {
      if ((isFF || isFF === undefined) && !hasNoClaimKey && hasMatchingClaim && hasRole) {
        return true;
      }
      return false;
    }
    if ((isFF || isFF === undefined) && !hasNoClaimKey && hasMatchingClaim) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimKey, claims, isFF, removedWriteOrRead, notCheckWriteOrRead, effectiveRoles]);

  // eslint-disable-next-line react/jsx-props-no-spreading
  return showComponent ? <Box {...containerProps}>{children}</Box> : null;
};
